import React, { useState } from "react";
import { FiSend, FiPaperclip } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const FullPageChat = () => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [hasInteracted, setHasInteracted] = useState(false); // Track user interaction

  const suggestedQueries = [
    "What are the advantages of using AI?",
    "Write code to demonstrate Dijkstra's algorithm",
    "Help me write an essay about AI ethics",
    "What is the future of AI?",
  ];

  // Mock AI Response Logic
  const generateMockResponse = (userInput) => {
    if (userInput.toLowerCase().includes("advantages of using ai")) {
      return "AI enhances efficiency, automates tasks, and provides deep insights from data.";
    }
    if (userInput.toLowerCase().includes("dijkstra")) {
      return "Sure! Here's a simple Dijkstra's algorithm implementation in Python:\n```python\nimport heapq\n\ndef dijkstra(graph, start):\n  # Implementation here...\n```";
    }
    if (userInput.toLowerCase().includes("ai ethics")) {
      return "AI ethics involve fairness, transparency, and accountability in AI decision-making.";
    }
    if (userInput.toLowerCase().includes("future of ai")) {
      return "The future of AI lies in automation, AGI research, and enhanced human-AI collaboration.";
    }
    return "I'm not sure, but I can help with something else!";
  };

  const handleSend = () => {
    if (input.trim()) {
      if (!hasInteracted) setHasInteracted(true); // Hide intro & queries when first message is sent

      const userMessage = { text: input, sender: "user" };
      const botResponse = { text: generateMockResponse(input), sender: "bot" };

      setMessages([...messages, userMessage, botResponse]);
      setInput("");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header Section - Hides on First Interaction */}
      <AnimatePresence>
        {!hasInteracted && (
          <motion.div
            className="flex flex-col items-center text-center pt-20"
            initial={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {/* Logo */}
            <div className="mb-6">
              <motion.div
                className="flex items-center space-x-2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1 }}
              >
                <span className="text-xl">▲</span>
                <span className="text-xl">💬</span>
              </motion.div>
            </div>

            {/* Introduction Text */}
            <motion.p
              className="text-lg md:text-xl text-gray-300 max-w-lg text-center leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              Welcome to{" "}
              <span className="font-extrabold bg-white text-transparent bg-clip-text">
                neUro Ki
              </span>
              , your AI-powered assistant. Ask anything about{" "}
              <span className="text-white">AI, automation</span>, and beyond.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Suggested Queries - Hides on First Interaction */}
      <AnimatePresence>
        {!hasInteracted && (
          <motion.div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl mx-auto"
            initial={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {suggestedQueries.map((query, index) => (
              <button
                key={index}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-gray-300 text-sm hover:bg-gray-800 transition"
                onClick={() => {
                  setInput(query);
                  handleSend();
                }}
              >
                {query}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Messages */}
      <div className="flex-1 w-full max-w-2xl mx-auto overflow-y-auto px-4 py-8 space-y-4">
        {messages.map((msg, index) => (
          <motion.div
            key={index}
            className={`p-3 rounded-lg w-fit max-w-[75%] ${msg.sender === "user"
              ? "ml-auto bg-white text-black"
              : "bg-black text-white"
              }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            {msg.text}
          </motion.div>
        ))}
      </div>

      {/* Chat Input Box - Full Width */}
      <motion.div
        className="w-full h-[100px] fixed bottom-4 left-0 flex justify-center px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.8 }}
      >
        <div className="w-full max-w-2xl bg-black/50 backdrop-blur-md rounded-3xl flex items-center px-4 py-4 shadow-lg border border-gray-700">
          {/* File Attachment Button */}
          <button className="text-gray-500 hover:text-white transition">
            <FiPaperclip size={22} />
          </button>

          {/* Input Field */}
          <input
            type="text"
            placeholder="Send a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            className="flex-1 bg-transparent text-white px-4 outline-none placeholder-gray-400 text-md"
          />

          {/* Send Button (Circular) */}
          <button
            onClick={handleSend}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-700 hover:bg-gray-600 transition text-white ml-2"
          >
            <FiSend size={18} />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default FullPageChat;
